import { GrpcStatusCode } from '@protobuf-ts/grpcweb-transport'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { usePermission } from '@/composables/permission'
import { useApiStore } from '@/stores/api'
import { useFlagStore } from '@/stores/flag'

import { hotReloadStore } from '@/utils/build'

import type { PermissionFallback, PermissionName } from '@/composables/permission'
import type { GetCapabilitiesRequest } from '@policyfly/protobuf/patrick'
import type { Capability, Condition, Status } from '@policyfly/protobuf/pfcapability'
import type { ComputedRef, Ref } from 'vue'

export interface CapabilityStore {
  /**
   * The capabilities available for the most recently loaded request.
   */
  capabilities: Ref<Capability[]>
  /**
   * The conditions affecting the object of the most recently loaded request.
   */
  conditions: Ref<Condition[]>
  /**
   * Whether capabilities are enabled by the `pfcapability-enabled` flag.
   * While disabled, every check returns its fallback instead.
   */
  enabled: ComputedRef<boolean>
  /**
   * Checks if a capability is available for the most recently loaded request.
   *
   * @param capability The capability to check if the user has.
   * @param fallback The permission (and item, if applicable) to check instead while the `pfcapability-enabled` flag is off.
   */
  checkCapability: <T extends PermissionName>(capability: Capability, fallback: PermissionFallback<T>) => boolean
  /**
   * Returns the first condition matching the provided status that affects the object of the most recently loaded request,
   * or `null` if there is no match.
   *
   * @param status The status to check for.
   * @param fallback The condition (or `null`) to return instead while the `pfcapability-enabled` flag is off.
   */
  checkCondition: (status: Status, fallback: Condition | null) => Condition | null
  /**
   * Loads the capabilities for a request, replacing any previously loaded capabilities.
   * Any load still in flight is aborted, so only the latest request is applied.
   *
   * @param request The object to load capabilities for, or omit to only load global capabilities.
   */
  load: (request?: GetCapabilitiesRequest) => Promise<void>
  /**
   * Reloads the capabilities for the most recently loaded request.
   */
  reload: () => Promise<void>
}

/**
 * Holds the capabilities available to the user on the current page.
 */
export const useCapabilityStore = defineStore('capability', (): CapabilityStore => {
  const capabilities: CapabilityStore['capabilities'] = ref([])
  const conditions: CapabilityStore['conditions'] = ref([])

  const flagStore = useFlagStore()
  const enabled: CapabilityStore['enabled'] = computed(() => flagStore.getFlag('pfcapability-enabled').enabled)

  const { checkPermission } = usePermission()
  const checkCapability: CapabilityStore['checkCapability'] = (capability, [permission, item]) => {
    if (enabled.value) {
      return capabilities.value.includes(capability)
    }
    return checkPermission(permission, item)
  }

  const checkCondition: CapabilityStore['checkCondition'] = (status, fallback) => {
    if (!enabled.value) return fallback
    return conditions.value.find((condition) => condition.status === status) ?? null
  }

  let lastRequest: GetCapabilitiesRequest | undefined
  let activeController: AbortController | null = null
  const apiStore = useApiStore()
  const load: CapabilityStore['load'] = async (request) => {
    lastRequest = request
    activeController?.abort(GrpcStatusCode[GrpcStatusCode.CANCELLED])
    const abortController = new AbortController()
    activeController = abortController
    try {
      const response = await apiStore.capability.list(request, abortController)
      if (abortController.signal.aborted) return
      capabilities.value = response.capabilities
      conditions.value = response.conditions
    } catch (err) {
      if (abortController.signal.aborted) return
      console.error(err)
      capabilities.value = []
      conditions.value = []
    }
  }

  const reload: CapabilityStore['reload'] = async () => {
    await load(lastRequest)
  }

  return {
    capabilities,
    conditions,
    enabled,
    checkCapability,
    checkCondition,
    load,
    reload,
  }
})

hotReloadStore(useCapabilityStore)
